<template>
<div class="emote-picker" :class="{ 'is-active': isPopperActive }">
    <transition name="fade">
        <Loader v-if="isLoading" overlay/>
        <VEmojiPicker
            v-else
            v-click-outside="hidePopper"
            @mouseover.native="showHoveredEmoteAlias"
            @select="onEmoteSelected"
            :customEmojis="allEmotes"
            :customCategories="customCategories"
            :initialCategory="$const.BTTV"
            :limitFrequently="100"
            :emojiSize="30"
            :emojisByRow="8"
            :labelSearch="searchPlaceholder"/>
    </transition>
</div>
</template>

<script>
// This is a singleton component. Only 1 is displayed at any time.
// It's really heavy. Manages thousands of elements.

import { createPopper } from '@popperjs/core'
import { mapGetters } from 'vuex'
import VEmojiPicker, { emojisDefault, categoriesDefault } from 'v-emoji-picker'

import * as C from '@shared/constants'
import * as M from '@/store/mutations'
import * as _ from '@/lodash'
import * as utils from '@/utils'

const DEFAULT_PLACEHOLDER = 'Search'

export default {
    name: 'EmotePicker',
    props: {
        channelId: String,
        lastUserActivityDate: Date,
    },
    data: vm => ({
        isPopperActive: false,
        customCategories: [],
        searchPlaceholder: DEFAULT_PLACEHOLDER,
        isLoading: false,
    }),
    computed: {
        ...mapGetters([
            'popperReferenceSelector',
            'selectedEmote',
            'allEmotes',
            'bttvEmotes',
            'bttvChannelId',
            'isMobile',
        ]),
        hasFetchedBttvEmotes: vm => (
            vm.bttvEmotes.length > 0 && vm.channelId === vm.bttvChannelId
        ),
    },
    watch: {
        async popperReferenceSelector (selector) {
            const $ref = document.querySelector(selector)
            this.isPopperActive = !!$ref

            if ($ref) {
                this.updatePopperReference($ref)
            }

            // Lazy load the component & emotes, so I don't DDOS my server.
            // Also, the import() fails randomly, so just check if the
            // component is loaded on every toggle.
            if (!this.hasFetchedBttvEmotes) {
                await this.fetchBTTVEmotes()
            }

            // Prevent the mobile keyboard from auto appearing, it takes up
            // too much space.
            if (!this.isMobile) {
                this.$el.querySelector('#InputSearch input')?.focus()
            }
        },
        // Slight optimization. Load emotes when user hovers the app, rather
        // than waiting for them to click an emote trigger.
        lastUserActivityDate: 'fetchBTTVEmotes',
    },
    mounted () {
        this.initPopper()

        const ignore = ['Places']
        const emotes = this.getDefaultEmotes(emojisDefault, ignore)
        this.$store.commit(M.SET_DEFAULT_EMOTES, { emotes })

        this.customCategories = this.getEmoteCategories(categoriesDefault, ignore)
    },
    methods: {
        async fetchBTTVEmotes () {
            if (this.isLoading || this.hasFetchedBttvEmotes) {
                return
            }

            try {
                this.isLoading = true

                // If BTTV api call fails, just show picker with the default emojis.
                const types = [C.BTTV_TOP, C.BTTV_TRENDING, C.BTTV_GLOBAL]
                await Promise.all([
                    this.$store.dispatch('fetchBTTVEmotes', { types }),
                    this.$store.dispatch('fetchBTTVChannelEmotes'),
                ])
            } catch (err) {
                Sentry.captureException(err)
            } finally {
                this.isLoading = false
            }
        },
        showHoveredEmoteAlias: _.throttle(function (e) {
            const emoteWrapperClass = 'emoji' // set by v-emoji-picker
            if (!utils.eventPathContainsClass(e, emoteWrapperClass)) {
                this.searchPlaceholder = DEFAULT_PLACEHOLDER
                return
            }

            const emoteClass = 'emote'
            let $emoteImg = e.target

            if (!$emoteImg.classList?.contains(emoteClass)) {
                $emoteImg = $emoteImg.querySelector(`.${emoteClass}`)
                if (!$emoteImg) { return }
            }

            const alias = $emoteImg.getAttribute(utils.ALIAS_ATTR)
            this.searchPlaceholder = alias
        }, 50),
        getDefaultEmotes (emojis, ignore) {
            const emoteSize = C.EMOTE_SIZE_IN_PICKER

            const data = emojis
                .filter(e => !ignore.includes(e.category))
                .map(e => Object.freeze({
                    ...e,
                    emoji: e.data,
                    data: this.$emoteToHTML(e.data, e.aliases[0], emoteSize)
                }))

            return Object.freeze(data)
        },
        getEmoteCategories (categories, ignore) {
            categories = categories.filter(c => !ignore.includes(c.name))
            const index = categories.findIndex(c => c.name === 'Frequently')
            // Put frequently on far left, then BTTV.
            categories.splice(index + 1, 0, {
                name: C.BTTV,
                // Probably not allowed to use the BTTV logo
                icon: `<img alt="bttv" src="${require('@/assets/img/bttv-logo.png')}"/>`
            })
            return categories
        },
        onEmoteSelected (emojiObj) {
            const { category, id, emoji } = emojiObj
            const emojiOrEmoteId = category === C.BTTV ? id : emoji

            this.$store.commit(M.SET_SELECTED_EMOTE, { emote: emojiOrEmoteId })
        },
        async initPopper () {
            // Keep picker outside the viewport, it interferes with the devtools
            // element picker.
            const placeholderClass = 'popper-placeholder-ref'
            let $div = document.querySelector(`.${placeholderClass}`)
            if (!$div) {
                $div = document.createElement('div')
                $div.classList.add(placeholderClass)
                document.body.appendChild($div)
            }
            this.updatePopperReference($div)
        },
        // https://stackoverflow.com/questions/60795628/popper-not-sticking-to-ref-element-on-scroll/60824249#60824249
        updatePopperReference ($popperReference) {
            if (this.popper) {
                this.popper.destroy()
            }
            const $boundary = document.querySelector('.page-viewer .scroll-body')
            const $pickerEl = this.$el

            this.popper = createPopper($popperReference, $pickerEl, {
                placement: 'auto',
                modifiers: [
                    {
                        name: 'offset',
                        options: {
                            offset: [0, 5],
                        },
                    },
                    {
                        name: 'preventOverflow',
                        options: {
                            padding: 2,
                            boundary: $boundary,
                        }
                    },
                ]
            })
        },
        hidePopper (event = null) {
            if (event && utils.eventPathContainsClass(event, 'emote-popper-reference')) {
                return
            }
            if (this.isPopperActive) {
                this.$store.commit(M.SET_EMOTE_POPPER_REFERENCE_SELECTOR, { selector: null })
            }
        }
    },
    components: {
        VEmojiPicker
    }
}
</script>

<style lang="scss">
$picker-width: $sbox-panel-width - 20px;

.picker-width {
    width: $picker-width;
    max-width: $picker-width;
}

.emote-picker {
    @extend .picker-width;
    position: relative;
    display: flex;
    justify-content: center;
    height: 250px;
    z-index: 10;
    opacity: 0;
    border-radius: 4px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24);
    pointer-events: none;
    transition: opacity $default-transition-duration;
    background: #f0f0f0; // Same as #EmojiPicker

    &.is-active {
        pointer-events: all;
        opacity: 1;
    }
}
#EmojiPicker {
    @extend .picker-width;
    .container-emoji {
        height: 180px;
    }
    .container-search {
        padding: 0px 3px;
        input {
            padding: 4px 6px;
        }
    }
    .category {
        padding: 2px 0px;
        > span {
            @include flex-center-children;
        }
        img { // handle BTTV img, and other any custom imgs.
            height: 18px;
            width: auto;
        }
    }
    &[data-popper-reference-hidden] {
        // visibility: hidden;
        // pointer-events: none;
    }
}

.popper-placeholder-ref {
    position: fixed;
    left: -350px;
}

#app.dark-theme .emoji.border:hover {
    //background-color: rgba($dark-theme-bg-color-lighter, 0.65);
    background-color: rgba($offwhite, 0.5);
}
</style>
